import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Users, TrendingUp, Wallet, Coins, AlertTriangle, Cake, ArrowUpRight } from "lucide-react";
import { SECTIONS, sectionLabel, sectionTag } from "@/lib/sections";

const fmtEUR = (n) => `€${Number(n || 0).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function KpiCards({ stats }) {
  const kpis = [
    { key: "total_members", label: "Active Members", value: stats?.total_members ?? "—", icon: Users, color: "text-primary" },
    { key: "attendance_rate_30d", label: "Attendance (30d)", value: stats ? `${stats.attendance_rate_30d}%` : "—", icon: TrendingUp, color: "text-green-700" },
    { key: "fees_this_month", label: "Fees This Month", value: stats ? fmtEUR(stats.fees_this_month) : "—", icon: Coins, color: "text-amber-700" },
    { key: "cash_balance", label: "Cash Balance", value: stats ? fmtEUR(stats.cash_balance) : "—", icon: Wallet, color: "text-emerald-700" },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {kpis.map(({ key, label, value, icon: Icon, color }) => (
        <Card key={key} data-testid={`kpi-${key}`} className="card-shadow-soft">
          <CardContent className="p-5">
            <div className={`w-10 h-10 rounded-lg bg-secondary flex items-center justify-center mb-3 ${color}`}><Icon className="w-5 h-5" /></div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">{label}</div>
            <div className="text-3xl font-extrabold mt-1" style={{ fontFamily: "Outfit" }}>{value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function SectionCards({ counts }) {
  return (
    <Card className="card-shadow-soft">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Sections</h2>
          <Link to="/members" className="text-sm text-primary font-semibold hover:underline" data-testid="link-members">View register →</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {SECTIONS.map((s) => (
            <Link key={s.value} to={`/members?section=${s.value}`} className="block" data-testid={`section-card-${s.value}`}>
              <div className="rounded-xl border border-border p-4 bg-card hover:border-primary transition-colors">
                <span className={`scout-tag ${s.tag} mb-3`}>{s.label}</span>
                <div className="text-3xl font-extrabold" style={{ fontFamily: "Outfit" }}>{counts?.[s.value] ?? 0}</div>
                <div className="text-xs text-muted-foreground mt-1">Ages {s.ages}</div>
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ActionTile({ to, testid, eyebrow, title, sub, alert }) {
  const cls = alert ? "border-red-300 bg-red-50 dark:bg-red-950/30 hover:border-red-500" : "border-border bg-card hover:border-primary";
  return (
    <Link to={to} data-testid={testid} className={`rounded-xl border p-5 card-shadow-soft ${cls}`}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{eyebrow}</div>
      <div className={`text-lg font-bold mt-1 ${alert ? "text-red-700 dark:text-red-300" : ""}`} style={{ fontFamily: "Outfit" }}>{title}</div>
      <div className="text-sm text-muted-foreground mt-1">{sub}</div>
    </Link>
  );
}

export function QuickActions({ overdueCount }) {
  const overdue = overdueCount > 0;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <ActionTile to="/fees" testid="overdue-banner" eyebrow="Fee status" alert={overdue}
        title={overdue ? `${overdueCount} member(s) overdue →` : "All fees up to date"}
        sub={overdue ? "Review and send reminders." : "Nothing outstanding right now."} />
      <ActionTile to="/attendance" testid="quick-attendance" eyebrow="Quick action" title="Record weekly attendance →" sub="Mark who was at Thursday's meeting." />
      <ActionTile to="/camps" testid="quick-camps" eyebrow="Quick action" title="Manage camp register →" sub="Sign-ups and camp fees in one place." />
    </div>
  );
}

const WHEN = { 0: "Today!", 1: "Tomorrow" };
const when = (b) => WHEN[b.days_until] ?? `In ${b.days_until} days`;

export function UpcomingBirthdays({ birthdays }) {
  if (!birthdays.length) return null;
  return (
    <Card className="card-shadow-soft border-sky-200 bg-sky-50 dark:bg-sky-950/30" data-testid="upcoming-birthdays">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Cake className="w-5 h-5 text-sky-700" />
          <h2 className="font-bold text-lg text-sky-900 dark:text-sky-200" style={{ fontFamily: "Outfit" }}>Upcoming birthdays</h2>
          <span className="text-xs text-sky-800/80 dark:text-sky-300">Next 14 days — give them a shout-out at the meeting</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {birthdays.map((b) => (
            <div key={b.member_id} data-testid={`birthday-${b.member_id}`} className="flex items-center justify-between gap-2 rounded-lg bg-card border border-border p-3">
              <div className="min-w-0">
                <div className="font-semibold truncate">{b.full_name}</div>
                <span className={`scout-tag ${sectionTag(b.section)}`}>{sectionLabel(b.section)}</span>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold" style={{ fontFamily: "Outfit" }}>Turning {b.turning}</div>
                <div className={`text-xs ${b.days_until === 0 ? "text-sky-700 font-semibold" : "text-muted-foreground"}`}>{when(b)} · {b.birthday.slice(5)}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function SectionMoves({ moves, onMoved }) {
  if (!moves.length) return null;
  const move = async (m) => {
    if (!window.confirm(`Move ${m.full_name} from ${sectionLabel(m.section)} to ${sectionLabel(m.suggested_section)}?`)) return;
    try {
      await api.post(`/members/${m.member_id}/move-section`, { section: m.suggested_section });
      toast.success(`${m.full_name} moved to ${sectionLabel(m.suggested_section)}`);
      onMoved?.();
    } catch (e) {
      toast.error("Could not move member");
    }
  };
  return (
    <Card className="card-shadow-soft border-violet-200 bg-violet-50 dark:bg-violet-950/30" data-testid="section-moves">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <ArrowUpRight className="w-5 h-5 text-violet-700" />
          <h2 className="font-bold text-lg text-violet-900 dark:text-violet-200" style={{ fontFamily: "Outfit" }}>Ready to move up</h2>
          <span className="text-xs text-violet-800/80 dark:text-violet-300">Members who've reached the top age for their section</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {moves.map((m) => (
            <div key={m.member_id} data-testid={`section-move-${m.member_id}`}
              className="flex items-center justify-between gap-2 rounded-lg bg-card border border-border p-3">
              <div className="min-w-0">
                <div className="font-semibold truncate">{m.full_name} <span className="text-xs text-muted-foreground">· age {m.age}</span></div>
                <div className="flex items-center gap-1 mt-1">
                  <span className={`scout-tag ${sectionTag(m.section)}`}>{sectionLabel(m.section)}</span>
                  <ArrowUpRight className="w-3 h-3 text-muted-foreground" />
                  <span className={`scout-tag ${sectionTag(m.suggested_section)}`}>{sectionLabel(m.suggested_section)}</span>
                </div>
              </div>
              <Button size="sm" data-testid={`move-up-${m.member_id}`} onClick={() => move(m)} className="shrink-0 bg-violet-700 hover:bg-violet-800 text-white">
                Move to {sectionLabel(m.suggested_section)}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function AbsenceAlerts({ alerts, threshold = 3 }) {
  if (!alerts.length) return null;
  return (
    <Card className="card-shadow-soft border-amber-300 bg-amber-50 dark:bg-amber-950/30" data-testid="absence-alerts">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-700" />
          <h2 className="font-bold text-lg text-amber-900 dark:text-amber-200" style={{ fontFamily: "Outfit" }}>Absence alerts</h2>
          <span className="text-xs text-amber-800/80 dark:text-amber-300" data-testid="absence-threshold-note">{threshold}+ meetings missed in a row — worth checking in</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {alerts.map((a) => (
            <Link key={a.member_id} to={`/members?section=${a.section}`} data-testid={`absence-alert-${a.member_id}`}
              className="flex items-center justify-between gap-2 rounded-lg bg-card border border-border p-3 hover:border-amber-500 transition-colors">
              <div className="min-w-0">
                <div className="font-semibold truncate">{a.full_name}</div>
                <span className={`scout-tag ${sectionTag(a.section)}`}>{sectionLabel(a.section)}</span>
              </div>
              <span className="scout-tag bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 shrink-0">{a.missed_in_a_row} missed</span>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
